import { supabase } from './supabase';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type LogCategory = 'AUTH' | 'DATABASE' | 'API' | 'SYSTEM' | 'USER_ACTION' | 'SECURITY' | 'VALIDATION' | 'PERFORMANCE';

interface LogOptions {
  level: LogLevel;
  category: LogCategory;
  message: string;
  details?: Record<string, any>;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
}

class Logger {
  private async insertLog(options: LogOptions): Promise<void> {
    try {
      const { error } = await supabase
        .from('system_logs')
        .insert([{
          level: options.level,
          category: options.category,
          message: options.message,
          details: options.details || {},
          user_id: options.userId || null,
          ip_address: options.ipAddress || null,
          user_agent: options.userAgent || navigator.userAgent
        }]);

      if (error) {
        console.error('Failed to insert log:', error);
      }
    } catch (err) {
      console.error('Logger error:', err);
    }
  }

  debug(category: LogCategory, message: string, details?: Record<string, any>): void {
    console.debug(`[${category}]`, message, details);
    this.insertLog({ level: 'DEBUG', category, message, details });
  }

  info(category: LogCategory, message: string, details?: Record<string, any>): void {
    console.info(`[${category}]`, message, details);
    this.insertLog({ level: 'INFO', category, message, details });
  }

  warning(category: LogCategory, message: string, details?: Record<string, any>): void {
    console.warn(`[${category}]`, message, details);
    this.insertLog({ level: 'WARNING', category, message, details });
  }

  error(category: LogCategory, message: string, details?: Record<string, any>): void {
    console.error(`[${category}]`, message, details);
    this.insertLog({ level: 'ERROR', category, message, details });
  }

  critical(category: LogCategory, message: string, details?: Record<string, any>): void {
    console.error(`[${category}] CRITICAL:`, message, details);
    this.insertLog({ level: 'CRITICAL', category, message, details });
  }

  async logAuthEvent(event: string, success: boolean, details?: Record<string, any>): Promise<void> {
    const level = success ? 'INFO' : 'WARNING';
    await this.insertLog({
      level,
      category: 'AUTH',
      message: `Authentication event: ${event}`,
      details: { success, ...details }
    });
  }

  async logUserAction(action: string, details?: Record<string, any>): Promise<void> {
    await this.insertLog({
      level: 'INFO',
      category: 'USER_ACTION',
      message: action,
      details
    });
  }

  async logApiCall(endpoint: string, method: string, status: number, duration?: number): Promise<void> {
    const level = status >= 400 ? 'ERROR' : status >= 300 ? 'WARNING' : 'INFO';
    await this.insertLog({
      level,
      category: 'API',
      message: `${method} ${endpoint} - ${status}`,
      details: { method, endpoint, status, duration }
    });
  }

  async logDatabaseOperation(operation: string, table: string, success: boolean, error?: any): Promise<void> {
    const level = success ? 'INFO' : 'ERROR';
    await this.insertLog({
      level,
      category: 'DATABASE',
      message: `${operation} on ${table}`,
      details: { operation, table, success, error: error?.message }
    });
  }

  async logSecurityEvent(event: string, severity: 'low' | 'medium' | 'high' | 'critical', details?: Record<string, any>): Promise<void> {
    const levelMap = {
      low: 'INFO' as LogLevel,
      medium: 'WARNING' as LogLevel,
      high: 'ERROR' as LogLevel,
      critical: 'CRITICAL' as LogLevel
    };

    await this.insertLog({
      level: levelMap[severity],
      category: 'SECURITY',
      message: `Security event: ${event}`,
      details: { severity, ...details }
    });
  }

  async logPerformance(operation: string, duration: number, details?: Record<string, any>): Promise<void> {
    const level = duration > 5000 ? 'WARNING' : 'INFO';
    await this.insertLog({
      level,
      category: 'PERFORMANCE',
      message: `${operation} took ${duration}ms`,
      details: { operation, duration, ...details }
    });
  }

  async logValidationError(field: string, error: string, value?: any): Promise<void> {
    await this.insertLog({
      level: 'WARNING',
      category: 'VALIDATION',
      message: `Validation failed for ${field}: ${error}`,
      details: { field, error, value }
    });
  }

  async logSystemEvent(event: string, details?: Record<string, any>): Promise<void> {
    await this.insertLog({
      level: 'INFO',
      category: 'SYSTEM',
      message: event,
      details
    });
  }
}

export const logger = new Logger();

export default logger;
